/**
 * Guided Zoom setup wizard actions.
 *
 * Extracted from chatStore: these drive the S2S-app provisioning flow
 * (create app, capture credentials, information tab + scopes handoff,
 * finale) by injecting narration into the chat store and calling the
 * browser/connection endpoints. They own no state of their own.
 */
import { useChatStore } from './chatStore';
import { useSetupStore } from './setupStore';
import { useBrowserStore } from './browserStore';
import { useConnectionStore } from './connectionStore';
import { getToken } from '@/stores/authStore';
import { API_BASE } from '@/lib/config';

const chat = () => useChatStore.getState();

export async function submitAppName(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const setDrivingStatus = useBrowserStore.getState().setDrivingStatus;
    await chat().addUserMessage(trimmed);
    const conversationId = chat().currentConversationId ?? undefined;
    await chat().addAssistantMessage(`Creating your Server-to-Server app "${trimmed}"…`);
    setDrivingStatus(`Creating "${trimmed}"…`);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/browser/zoom-create-s2s-app`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ name: trimmed, conversationId }),
      });
      const data = (await res.json().catch(() => ({}))) as { created?: boolean; error?: string };
      if (res.ok && data.created) {
        useSetupStore.getState().setAppCreated(true);
        await chat().addAssistantMessage(`Done — "${trimmed}" is created.`);
        // Chain the two capture steps (raw fetch — these endpoints aren't in the
        // generated client until `encore gen client`). Each is defensive: a
        // failure reports and stops the chain rather than throwing.
        const post = async (path: string, payload: Record<string, unknown> = {}) => {
          const r = await fetch(`${API_BASE}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
            body: JSON.stringify(payload),
          });
          return r.json().catch(() => ({}));
        };
        // Step 1 — capture Account ID / Client ID / Client Secret (encrypted).
        // Pass the app name so it's stored alongside the credentials.
        setDrivingStatus('Reading your app credentials…');
        const cap = (await post('/browser/zoom-capture-credentials', { name: trimmed, conversationId })) as { captured?: boolean; clientIdLast4?: string; error?: string };
        if (!cap.captured) {
          useBrowserStore.getState().setAgentControl(false);
          await chat().addAssistantMessage(
            `The app is created, but I couldn't read its credentials off the page${cap.error ? ` (${cap.error})` : ''}. ` +
              `Make sure the **App Credentials** view is showing in the browser, then hit retry.`,
            undefined,
            {
              type: 'choice',
              options: [
                { id: 'zoom-setup-retry-capture', label: 'Retry credential capture', value: 'zoom-setup:retry-capture', highlight: true },
              ],
            },
          );
        } else {
          useSetupStore.getState().setCredsCaptured(true);
          await chat().addAssistantMessage(
            `Saved your app credentials securely — client ID ending \`${cap.clientIdLast4}\`. The secret is stored encrypted; I never keep it in the clear.`,
          );
          // Next phase: Information tab (activation prerequisites) + the
          // scopes handoff. Activation, identity and verification follow once
          // the user confirms the scopes are in.
          await runInfoAndScopesHandoff();
        }
      } else {
        await chat().addAssistantMessage(
          `I couldn't finish creating the app${data.error ? ` (${data.error})` : ''}. You can take over and finish it manually, or tell me to retry.`,
        );
      }
    } catch {
      await chat().addAssistantMessage(
        "I couldn't reach the browser to create the app. Take over to finish manually, or tell me to retry.",
      );
    } finally {
      // Whatever happened, the drive is over — never leave a stale status pill.
      setDrivingStatus(null);
    }
  }

export async function retryCredentialCapture() {
    const setDrivingStatus = useBrowserStore.getState().setDrivingStatus;
    const token = getToken();
    const conversationId = chat().currentConversationId ?? undefined;
    try {
      useBrowserStore.getState().setAgentControl(true);
      setDrivingStatus('Reading your app credentials…');
      const r = await fetch(`${API_BASE}/browser/zoom-capture-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ conversationId }),
      });
      const cap = (await r.json().catch(() => ({}))) as { captured?: boolean; clientIdLast4?: string; error?: string };
      if (!cap.captured) {
        useBrowserStore.getState().setAgentControl(false);
        await chat().addAssistantMessage(
          `Still couldn't read the credentials${cap.error ? ` (${cap.error})` : ''}. Check the browser is on the **App Credentials** view and retry — or add them manually in Settings.`,
          undefined,
          {
            type: 'choice',
            options: [
              { id: 'zoom-setup-retry-capture-again', label: 'Retry credential capture', value: 'zoom-setup:retry-capture', highlight: true },
            ],
          },
        );
        return;
      }
      useSetupStore.getState().setCredsCaptured(true);
      await chat().addAssistantMessage(
        `Got them this time — client ID ending \`${cap.clientIdLast4}\`, secret stored encrypted. Moving on.`,
      );
      await runInfoAndScopesHandoff();
    } catch {
      useBrowserStore.getState().setAgentControl(false);
      await chat().addAssistantMessage("I couldn't reach the browser to retry. Tell me to retry once it's back.");
    } finally {
      setDrivingStatus(null);
    }
  }

export async function runInfoAndScopesHandoff() {
    const setDrivingStatus = useBrowserStore.getState().setDrivingStatus;
    const token = getToken();
    const conversationId = chat().currentConversationId ?? undefined;
    const post = async (path: string, payload: Record<string, unknown> = {}) => {
      const r = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ conversationId, ...payload }),
      });
      return r.json().catch(() => ({}));
    };
    try {
      useBrowserStore.getState().setAgentControl(true);
      setDrivingStatus('Filling in your app details…');
      const info = (await post('/browser/zoom-fill-app-info')) as { filled?: boolean; devName?: string; company?: string; error?: string };
      if (info.filled) {
        useSetupStore.getState().setInfoFilled(true);
        await chat().addAssistantMessage(
          `Filled in your app's contact details (${info.devName}, ${info.company}) — Zoom requires them before the app can be activated.`,
          undefined, undefined, { once: true },
        );
      } else {
        await chat().addAssistantMessage(
          `I couldn't fill the app's contact details${info.error ? ` (${info.error})` : ''}. I'll add the scopes and try again before activating.`,
          undefined, undefined, { once: true },
        );
      }
      // Scopes — ZAC drives the Add-Scopes modal itself (per-scope search and
      // tick). The backend picks the list: the core read set, plus the core
      // write set when the user chose "Read + write". Partial success
      // proceeds; the verify step reports what Zoom actually granted. Manual
      // instructions remain only as the fallback when the modal can't be
      // driven at all.
      const write = useSetupStore.getState().accessLevel === 'writer';
      setDrivingStatus(write ? 'Adding read + write scopes…' : 'Adding read scopes…');
      const sc = (await post('/browser/zoom-add-scopes', { write })) as { added?: string[]; alreadyPresent?: string[]; failed?: string[]; error?: string };
      const addedCount = (sc.added?.length ?? 0) + (sc.alreadyPresent?.length ?? 0);
      const failedCount = sc.failed?.length ?? 0;
      if (addedCount > 0) {
        useSetupStore.getState().setScopesConfirmed(true);
        await chat().addAssistantMessage(
          `Added **${addedCount} ${write ? 'read + write' : 'read'} scopes** to your app` +
            (failedCount > 0 ? ` (${failedCount} didn't take — I'll verify what Zoom actually granted at the end)` : '') +
            `. Activating…`,
          undefined, undefined, { once: true },
        );
        await finishGuidedSetup();
      } else {
        // Total miss — fall back to the manual handoff with a resume button.
        useBrowserStore.getState().setAgentControl(false);
        await chat().addAssistantMessage(
          `I couldn't drive the scopes dialog${sc.error ? ` (${sc.error})` : ''} — your turn for a moment. In the browser on the left:\n\n` +
            `1. Open the **Scopes** tab\n` +
            `2. Click **+ Add Scopes**\n` +
            `3. Add the read scopes you want ZAC to have (account, user, meeting, report)\n` +
            `4. Click **Done**\n\n` +
            `Then hit the button below and I'll activate the app and verify everything.`,
          undefined,
          {
            type: 'choice',
            options: [
              { id: 'zoom-setup-scopes-added', label: "I've added the scopes — continue", value: 'zoom-setup:scopes-added', highlight: true },
            ],
          },
          { once: true },
        );
      }
    } finally {
      setDrivingStatus(null);
    }
  }

export async function finishGuidedSetup() {
    const setDrivingStatus = useBrowserStore.getState().setDrivingStatus;
    const token = getToken();
    const conversationId = chat().currentConversationId ?? undefined;
    const post = async (path: string, payload: Record<string, unknown> = {}) => {
      const r = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ conversationId, ...payload }),
      });
      return r.json().catch(() => ({}));
    };
    try {
      useSetupStore.getState().setScopesConfirmed(true);
      useBrowserStore.getState().setAgentControl(true);

      // 1. Activate — Zoom refuses with a verbatim blocker list if anything's
      //    still missing; we surface it and re-offer the resume button. Contact
      //    details Zoom dropped earlier are filled again first, so "try again"
      //    retries them too.
      if (!useSetupStore.getState().infoFilled) {
        setDrivingStatus('Filling in your app details…');
        const info = (await post('/browser/zoom-fill-app-info')) as { filled?: boolean };
        if (info.filled) useSetupStore.getState().setInfoFilled(true);
      }
      setDrivingStatus('Activating your app…');
      const act = (await post('/browser/zoom-activate-app')) as { activated?: boolean; blockers?: string[]; error?: string };
      if (!act.activated) {
        useBrowserStore.getState().setAgentControl(false);
        const blockerList = act.blockers?.length ? `\n\nZoom says it still needs:\n${act.blockers.map((b) => `- ${b}`).join('\n')}` : '';
        const onInfoTab = !!act.blockers?.length && act.blockers.every((b) => /contact|company/i.test(b));
        await chat().addAssistantMessage(
          `The app couldn't be activated yet${act.error && !blockerList ? ` (${act.error})` : ''}.${blockerList}\n\n${onInfoTab ? `Fill ${act.blockers?.length === 1 ? 'it' : 'them'} in on the app's **Information** tab in the browser` : 'Sort that out in the editor'}, then hit the button again.`,
          undefined,
          {
            type: 'choice',
            options: [
              { id: 'zoom-setup-scopes-added-retry', label: 'Done — try again', value: 'zoom-setup:scopes-added', highlight: true },
            ],
          },
        );
        return;
      }
      useSetupStore.getState().setActivated(true);
      await chat().addAssistantMessage(`Your app is **activated** on Zoom. ✅`, undefined, undefined, { once: true });

      // 2. Identity — records the connection row (flips "connected").
      setDrivingStatus('Confirming your Zoom account…');
      const idn = (await post('/browser/zoom-capture-identity')) as { connected?: boolean; email?: string; error?: string };
      if (!idn.connected) {
        useBrowserStore.getState().setAgentControl(false);
        await chat().addAssistantMessage(
          `Activated, but I couldn't confirm your Zoom account from the profile page${idn.error ? ` (${idn.error})` : ''}. Tell me to retry or check the **Account** page.`,
        );
        return;
      }

      // 3. Verify — mint a REAL token; what Zoom granted is the ground truth.
      setDrivingStatus('Verifying the connection…');
      const ver = (await post('/connection/s2s/verify')) as { verified?: boolean; grantedCount?: number; missing?: string[]; canWrite?: boolean; missingWrite?: string[]; error?: string };
      useBrowserStore.getState().setAgentControl(false);

      // Refresh server-derived state so the stepper/Account page flip live.
      await Promise.all([
        useConnectionStore.getState().checkConnection(),
        useSetupStore.getState().loadSetupState(),
      ]);

      if (!ver.verified) {
        await chat().addAssistantMessage(
          `You're connected${idn.email ? ` as **${idn.email}**` : ''}, but the connection test failed${ver.error ? ` — Zoom said: *${ver.error}*` : ''}. ` +
            `Usually that means the app isn't fully active yet or has no scopes. Fix it in the editor, then hit retry.`,
          undefined,
          {
            type: 'choice',
            options: [
              { id: 'zoom-setup-verify-retry', label: 'Retry verification', value: 'zoom-setup:scopes-added', highlight: true },
            ],
          },
        );
        return;
      }

      const missingCount = ver.missing?.length ?? 0;
      const scopeLine = missingCount === 0
        ? `- Scopes: **all ${ver.grantedCount} ${ver.canWrite ? '' : 'read '}scopes granted**\n`
        : `- Scopes: **${ver.grantedCount} granted** (${missingCount} of the requested read scopes not granted — I'll work with what I have; add more anytime)\n`;
      // A "Read + write" setup: say what write access covers, and which write
      // scopes Zoom withheld, if any — a change that needs one fails at Zoom
      // with its reason, and the user will know why.
      const missingWrite = ver.missingWrite ?? [];
      const writeLine = ver.canWrite
        ? `- Write access: **on**` + (missingWrite.length > 0 ? ` (Zoom didn't grant ${missingWrite.join(', ')} — changes that need them will say so)` : '') + `\n`
        : '';
      const closing = ver.canWrite
        ? `\nI can read your Zoom configuration, run audits, and apply the changes you ask for. Ask me anything.`
        : `\nI can read your Zoom configuration and run audits now. Ask me anything.`;
      await chat().addAssistantMessage(
        `**You're connected${idn.email ? ` as ${idn.email}` : ''} — and verified.** ✅\n\n` +
          `- App: activated, token test passed\n` +
          scopeLine +
          writeLine +
          closing,
        undefined,
        {
          type: 'choice',
          options: [
            { id: 'zoom-setup-generate-report', label: 'Generate report', value: 'zoom-setup:generate-report', highlight: true },
          ],
        },
      );
    } catch {
      useBrowserStore.getState().setAgentControl(false);
      await chat().addAssistantMessage(
        "Something went wrong finishing the setup — the browser may have dropped. Tell me to retry.",
      );
    } finally {
      setDrivingStatus(null);
    }
  }

